import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Bell, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import BranchLabel from './ui/BranchLabel';
import useBranchLabels from '../hooks/useBranchLabels';

const Announcements = () => {
  const { user, canManageFeature } = useAuth();
  const { showBranchLabels } = useBranchLabels();
  const canManageAnnouncements = canManageFeature('announcements');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const { toast } = useToast();

  const closeCreateDialog = () => {
    flushSync(() => {
      setIsDialogOpen(false);
      setSubmitting(false);
    });
    submittingRef.current = false;
    setFormData({ title: '', content: '', target_audience: 'all', priority: 'medium' });
  };

  const [formData, setFormData] = useState({
    title: '', content: '', target_audience: 'all', priority: 'medium',
  });

  useEffect(() => { fetchAnnouncements(); }, [user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchAnnouncements();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id]);

  const fetchAnnouncements = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const response = await api.get('/announcements', {
        params: {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        },
      });
      setAnnouncements(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch announcements', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    let succeeded = false;
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      await api.post('/announcements', {
        ...formData,
        ...(schoolId ? { school_id: schoolId } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      });
      succeeded = true;
      closeCreateDialog();
      toast({ title: 'Success', description: 'Announcement published' });
      fetchAnnouncements();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    } finally {
      if (!succeeded) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  const priorityColors = {
    low: 'bg-muted text-muted-foreground border-border',
    medium: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    high: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Announcements</h1>
          <p className="text-muted-foreground">Broadcast messages to your school</p>
        </div>
        {canManageAnnouncements && (
          <Button
            type="button"
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-announcement-btn"
            onClick={() => setIsDialogOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" /> Create Announcement
          </Button>
        )}
      </div>

      {canManageAnnouncements && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="border-border text-foreground">
            <DialogHeader><DialogTitle>Create Announcement</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="bg-muted border-border" required />
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="bg-muted border-border min-h-24" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Target Audience</Label>
                  <Select value={formData.target_audience} onValueChange={(v) => setFormData({...formData, target_audience: v})}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      <SelectItem value="all">Everyone</SelectItem>
                      <SelectItem value="students">Students</SelectItem>
                      <SelectItem value="teachers">Teachers</SelectItem>
                      <SelectItem value="parents">Parents</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700">
                {submitting ? 'Publishing…' : 'Publish'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : announcements.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No announcements yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map(ann => (
            <Card key={ann.id} className={`bg-card border ${priorityColors[ann.priority] || priorityColors.medium}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    ann.priority === 'high' ? 'bg-red-500/20' : 'bg-blue-500/20'
                  }`}>
                    {ann.priority === 'high' ? <AlertCircle className="w-5 h-5 text-red-400" /> : <Bell className="w-5 h-5 text-blue-400" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-foreground text-lg flex items-center gap-2 flex-wrap">
                        {ann.title}
                        <BranchLabel name={ann.branch_name} show={showBranchLabels} />
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs capitalize ${priorityColors[ann.priority]}`}>
                        {ann.priority}
                      </span>
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">{ann.content}</p>
                    <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                      <span>For: {ann.target_audience}</span>
                      <span>{ann.created_at ? new Date(ann.created_at).toLocaleDateString() : '—'}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Announcements;
